/**
 * Dependency-free date helpers that replace the `moment` calls which were forcing
 * moment into the **eager** initial bundle.
 *
 * Only the shell/eager code paths were migrated (notification-sidebar, the models
 * re-exported by the `app/model` barrel). moment remains in the lazy feature modules
 * (holiday, assembly-view) where it loads in their own chunks.
 *
 * Behavior matches the exact moment calls replaced:
 *  - date-only ISO strings (`YYYY-MM-DD`) parse as **local** midnight, like `moment()`
 *    (native `new Date('YYYY-MM-DD')` would parse as UTC and shift the day).
 *  - `.utc().format('DD/MM/YYYY')` formats the UTC calendar date.
 *  - `isBefore/isAfter(other, 'day')` compare at day granularity in local time.
 */

const pad = (n: number): string => (n < 10 ? '0' + n : '' + n);

/** Parse like `moment(value)`: date-only ISO → local midnight; everything else
 *  defers to the native parser (slash dates → local, full ISO → offset-aware). */
export function parseLocalDate(value: string | Date): Date {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
}

/** `moment(value).format('DD/MM/YYYY')` — local time. */
export function formatDDMMYYYY(value: string | Date): string {
  const d = parseLocalDate(value);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** `moment(value).format('YYYY-MM-DD')` (default) or `'YYYY/MM/DD'` (`sep = '/'`) — local time. */
export function formatISODate(value: string | Date, sep: '-' | '/' = '-'): string {
  const d = parseLocalDate(value);
  return `${d.getFullYear()}${sep}${pad(d.getMonth() + 1)}${sep}${pad(d.getDate())}`;
}

/** `moment(value).utc().format('DD/MM/YYYY')` — UTC calendar date. */
export function formatUtcDDMMYYYY(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

/** Local start-of-day timestamp, for day-granularity comparisons. */
function startOfDay(value: string | Date): number {
  const d = parseLocalDate(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** `moment(a).isBefore(moment(b), 'day')`. */
export function isBeforeDay(a: string | Date, b: string | Date): boolean {
  return startOfDay(a) < startOfDay(b);
}

/** `moment(a).isAfter(moment(b), 'day')`. */
export function isAfterDay(a: string | Date, b: string | Date): boolean {
  return startOfDay(a) > startOfDay(b);
}
